import type { PropsWithChildren, Ref } from 'react';
import { useRef } from 'react';

import { Toasty } from '@opendoor/atoms/toast';
import type { LinkRenderProps, RenderLink } from '@opendoor/atoms/utils';
import { AtomsPortalProvider, LinkProvider } from '@opendoor/atoms/utils';
import { Link as RouterLink, useInRouterContext } from 'react-router';

const isInAppPath = (target: string) => target.startsWith('/');

const AtomsLink = ({
  href,
  to,
  ref,
  ...rest
}: LinkRenderProps & { ref?: Ref<HTMLAnchorElement> }) => {
  const target = to ?? href ?? '';
  // App chrome above <BrowserRouter> (e.g. ImpersonationBanner) also renders
  // Atoms links; RouterLink throws outside a router.
  const canRoute = useInRouterContext() && isInAppPath(target);

  if (canRoute) {
    return <RouterLink ref={ref} to={target} {...rest} />;
  }

  // eslint-disable-next-line jsx-a11y/anchor-has-content -- children arrive via {...rest}
  return <a ref={ref} href={target} {...rest} />;
};

const renderAtomsLink: RenderLink = (props) => <AtomsLink {...props} />;

/**
 * Wires @opendoor/atoms into the app: React Router navigation, a high-z-index
 * portal root for overlays, and the app-wide toast viewport.
 */
export const AtomsAppProvider = ({ children }: PropsWithChildren) => {
  // Atoms overlays (Dialog/Combobox/Toast) portal to <body> at z-auto, so app
  // chrome that owns a stacking context paints over them. Route them into a
  // high-z-index root instead (mirrors cake and reception-fe).
  const atomsOverlayRoot = useRef<HTMLDivElement>(null);

  return (
    <LinkProvider renderLink={renderAtomsLink}>
      {/* Zero-size portal target; portaled children position themselves. */}
      <div id="atoms-overlay-root" ref={atomsOverlayRoot} className="fixed top-0 left-0 z-1000" />
      <AtomsPortalProvider container={atomsOverlayRoot}>
        {/* Toasty's viewport portals through the AtomsPortalProvider container
            above, so toasts share the overlay root with dialogs. It owns the
            toast manager that `useToast` (src/contexts/ToastContext) drives. */}
        <Toasty>{children}</Toasty>
      </AtomsPortalProvider>
    </LinkProvider>
  );
};
